<template>
  <div class="game-host" @pointerdown="onPointer">
    <canvas ref="canvas" class="game-canvas"></canvas>
    <textarea
      v-if="textInput.visible"
      ref="textInputEl"
      class="game-text-input"
      :style="textInputStyle"
      :value="textInput.value"
      :placeholder="textInput.placeholder"
      :maxlength="textInput.maxLength"
      rows="1"
      enterkeyhint="send"
      autocapitalize="sentences"
      autocomplete="off"
      spellcheck="true"
      @pointerdown.stop
      @input="onNativeTextInput"
      @keydown="onNativeTextInputKeydown"
    ></textarea>
    <div v-if="phase === 'loading'" class="game-loading"><span class="game-loading-dot"></span><strong>Starting game…</strong></div>
    <div v-if="error" class="game-error"><strong>Game Runtime</strong><span>{{ error }}</span></div>
  </div>
</template>

<script setup>
import { computed, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch } from 'vue'
import { lauxlib, lua, lualib, to_jsstring, to_luastring } from 'fengari'
import { authHeaders } from '@/stores/api/http'
import { reportClientError } from '@/stores/api/clientErrors'
import { normalizeImageSrc } from '@/composables/safeImage'
import { moduleAssetPath, preloadLuaModules } from './luaPackage.mjs'

const props = defineProps({
  installationId: { type: String, default: '' },
  draftPluginId: { type: String, default: '' },
  draftVersionId: { type: String, default: '' },
  revision: { type: [String, Number], default: 0 },
  liveSnapshot: { type: Function, required: true },
  tradeIntent: { type: Function, required: true },
  controlIntent: { type: Function, default: null },
  resetAbility: { type: Function, default: null },
  openCharacters: { type: Function, default: null },
  aiCommand: { type: Function, default: null },
  aiResult: { type: Function, default: null },
  aiHistory: { type: Function, default: null },
})
const emit = defineEmits(['status', 'metrics'])
const canvas = ref(null)
const textInputEl = ref(null)
const textInput = ref({ visible: false, x: 0, y: 0, width: 1, height: 1, value: '', placeholder: '', maxLength: 512 })
const textInputStyle = computed(() => ({
  left: `${textInput.value.x}px`,
  top: `${textInput.value.y}px`,
  width: `${textInput.value.width}px`,
  height: `${textInput.value.height}px`,
}))
const error = ref('')
const phase = ref('loading')
let L = null
let ctx = null
let frame = 0
let previous = 0
let instructionBudget = 0
let instructionBudgetExceeded = false
let disposed = false
let active = true
let generation = 0
let firstFrameTrace = null
let bootStartedAt = 0
let startupMs = 0
let assetTelemetryTimer = 0
let assetStats = { count: 0, bytes: 0 }
let assetPaths = new Set()
let moduleSources = new Map()
const moduleRefs = new Map()
const moduleLoading = new Set()
const images = new Map()
const marketImages = new Map()
const sounds = new Map()
const aiResults = new Map()
const aiResultLoads = new Set()
const aiResultCheckedAt = new Map()
let aiHistoryCache = []
let aiHistoryLoading = false
let aiHistoryFetchedAt = 0
const AI_READ_REFRESH_MS = 1000
const MAX_AI_RESULTS = 32
const MAX_TEXT_INPUT_LENGTH = 8000
const MAX_TEXT_INPUT_SUBMITS = 4
const textInputSubmits = []
let textInputDeclaredThisFrame = false
let hostCallName = ''
let hostCallError = ''

const SAFE_LIBS = [
  ['_G', lualib.luaopen_base],
  [lualib.LUA_COLIBNAME || 'coroutine', lualib.luaopen_coroutine],
  [lualib.LUA_TABLIBNAME || 'table', lualib.luaopen_table],
  [lualib.LUA_STRLIBNAME || 'string', lualib.luaopen_string],
  [lualib.LUA_MATHLIBNAME || 'math', lualib.luaopen_math],
  [lualib.LUA_UTF8LIBNAME || 'utf8', lualib.luaopen_utf8],
]

function openSafeLibraries(state) {
  for (const [name, fn] of SAFE_LIBS) {
    if (typeof fn !== 'function') continue
    lauxlib.luaL_requiref(state, to_luastring(name), fn, 1)
    lua.lua_pop(state, 1)
  }
  for (const name of ['dofile', 'loadfile', 'require', 'package', 'io', 'os', 'debug', 'js']) {
    lua.lua_pushnil(state)
    lua.lua_setglobal(state, to_luastring(name))
  }
}

function registerFunction(name, fn) {
  lua.lua_pushjsfunction(L, () => {
    hostCallName = name
    hostCallError = ''
    try {
      const result = fn()
      hostCallName = ''
      return result
    } catch (cause) {
      hostCallError = String(cause?.message || cause || 'host_callback_failed')
      throw cause
    }
  })
  lua.lua_setfield(L, -2, to_luastring(name))
}
function clearHostCall() {
  hostCallName = ''
  hostCallError = ''
}
function takeHostCallError() {
  if (!hostCallName) return ''
  const message = `host_api:${hostCallName}:${hostCallError || 'callback_failed'}`
  clearHostCall()
  return message
}
function numberArg(index, fallback = 0) {
  const value = lua.lua_tonumber(L, index)
  return Number.isFinite(value) ? value : fallback
}
function stringArg(index, fallback = '') {
  if (!lua.lua_isstring(L, index)) return fallback
  const bytes = lua.lua_tostring(L, index)
  try { return to_jsstring(bytes) } catch {
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes)
  }
}
function boolArg(index, fallback = false) {
  return lua.lua_isboolean(L, index) ? Boolean(lua.lua_toboolean(L, index)) : fallback
}
function scalarArg(index) {
  if (lua.lua_isboolean(L, index)) return Boolean(lua.lua_toboolean(L, index))
  if (lua.lua_isnumber(L, index)) return numberArg(index)
  if (lua.lua_isstring(L, index)) return stringArg(index)
  return null
}

function registerPackageRequire() {
  lua.lua_pushjsfunction(L, () => {
    const name = stringArg(1).trim()
    try { moduleAssetPath(name) } catch { return lauxlib.luaL_error(L, to_luastring('invalid_lua_module_name')) }
    const cached = moduleRefs.get(name)
    if (cached != null) { lua.lua_rawgeti(L, lua.LUA_REGISTRYINDEX, cached); return 1 }
    if (moduleLoading.has(name)) return lauxlib.luaL_error(L, to_luastring(`lua_module_cycle:${name}`))
    const source = moduleSources.get(name)
    if (source == null) return lauxlib.luaL_error(L, to_luastring(`lua_module_not_preloaded:${name}`))
    moduleLoading.add(name)
    resetBudget(500000)
    if (lauxlib.luaL_loadbuffer(L, to_luastring(source), null, to_luastring(`@${moduleAssetPath(name)}`)) !== lua.LUA_OK) {
      moduleLoading.delete(name)
      return lauxlib.luaL_error(L, to_luastring(luaError()))
    }
    if (lua.lua_pcall(L, 0, 1, 0) !== lua.LUA_OK) {
      moduleLoading.delete(name)
      return lauxlib.luaL_error(L, to_luastring(luaError()))
    }
    if (lua.lua_isnil(L, -1)) { lua.lua_pop(L, 1); lua.lua_pushboolean(L, true) }
    lua.lua_pushvalue(L, -1)
    const ref = lauxlib.luaL_ref(L, lua.LUA_REGISTRYINDEX)
    moduleRefs.set(name, ref)
    moduleLoading.delete(name)
    return 1
  })
  lua.lua_setglobal(L, to_luastring('require'))
}

function registerGameAPI() {
  lua.lua_newtable(L)
  registerFunction('width', () => { lua.lua_pushnumber(L, logicalWidth()); return 1 })
  registerFunction('height', () => { lua.lua_pushnumber(L, logicalHeight()); return 1 })
  registerFunction('clear', () => {
    if (!ctx) return 0
    ctx.save(); ctx.fillStyle = stringArg(1, '#050505'); ctx.fillRect(0, 0, logicalWidth(), logicalHeight()); ctx.restore(); return 0
  })
  registerFunction('rect', () => {
    if (!ctx) return 0
    ctx.save(); ctx.fillStyle = stringArg(5, '#ffffff'); ctx.fillRect(numberArg(1), numberArg(2), numberArg(3), numberArg(4)); ctx.restore(); return 0
  })
  registerFunction('circle', () => {
    if (!ctx) return 0
    ctx.save(); ctx.fillStyle = stringArg(4, '#ffffff'); ctx.beginPath(); ctx.arc(numberArg(1), numberArg(2), Math.max(0, numberArg(3)), 0, Math.PI * 2); ctx.fill(); ctx.restore(); return 0
  })
  registerFunction('line', () => {
    if (!ctx) return 0
    ctx.save(); ctx.strokeStyle = stringArg(5, '#ffffff'); ctx.lineWidth = Math.max(0.5, numberArg(6, 1)); ctx.beginPath(); ctx.moveTo(numberArg(1), numberArg(2)); ctx.lineTo(numberArg(3), numberArg(4)); ctx.stroke(); ctx.restore(); return 0
  })
  registerFunction('text', () => {
    if (!ctx) return 0
    ctx.save(); ctx.fillStyle = stringArg(4, '#ffffff'); ctx.font = `${Math.max(8, Math.min(96, numberArg(5, 16)))}px ui-sans-serif, system-ui, sans-serif`; ctx.textBaseline = 'top'; ctx.fillText(stringArg(1), numberArg(2), numberArg(3)); ctx.restore(); return 0
  })
  registerFunction('image', () => {
    const path = cleanAssetPath(stringArg(1)); if (!path || !ctx) return 0
    const item = ensureImage(path)
    if (item?.image?.complete && item.image.naturalWidth > 0) {
      const w = numberArg(4, item.image.naturalWidth), h = numberArg(5, item.image.naturalHeight)
      ctx.drawImage(item.image, numberArg(2), numberArg(3), w, h)
    }
    return 0
  })
  registerFunction('sprite', () => {
    const path = cleanAssetPath(stringArg(1)); if (!path || !ctx) return 0
    const item = ensureImage(path)
    if (item?.image?.complete && item.image.naturalWidth > 0) {
      const image = item.image
      const x = numberArg(2), y = numberArg(3)
      const w = Math.max(0, numberArg(4, image.naturalWidth)), h = Math.max(0, numberArg(5, image.naturalHeight))
      const sx = Math.max(0, numberArg(6)), sy = Math.max(0, numberArg(7))
      const sw = Math.max(1, numberArg(8, image.naturalWidth)), sh = Math.max(1, numberArg(9, image.naturalHeight))
      ctx.drawImage(image, sx, sy, sw, sh, x, y, w, h)
    }
    return 0
  })
  registerFunction('market_image', () => {
    const mint = stringArg(1).trim(); if (!mint || !ctx) return 0
    const item = ensureMarketImage(mint)
    if (item?.image?.complete && item.image.naturalWidth > 0) {
      const w = numberArg(4, item.image.naturalWidth), h = numberArg(5, item.image.naturalHeight)
      ctx.drawImage(item.image, numberArg(2), numberArg(3), w, h)
    }
    return 0
  })
  registerFunction('audio', () => {
    const path = cleanAssetPath(stringArg(1))
    if (!path) { lua.lua_pushboolean(L, false); return 1 }
    const action = stringArg(2, 'play').trim().toLowerCase() || 'play'
    const item = ensureAudio(path)
    if (!item?.audio) { lua.lua_pushboolean(L, action === 'load'); return 1 }
    const sound = item.audio
    sound.volume = Math.max(0, Math.min(1, numberArg(3, 1)))
    sound.loop = boolArg(4, false)
    if (action === 'stop') { sound.pause(); sound.currentTime = 0 }
    else if (action === 'pause') sound.pause()
    else if (action === 'restart') { sound.currentTime = 0; void sound.play().catch(() => {}) }
    else if (action === 'play') void sound.play().catch(() => {})
    else if (action !== 'load') { lua.lua_pushboolean(L, false); return 1 }
    lua.lua_pushboolean(L, true)
    return 1
  })
  registerFunction('text_input', () => {
    textInputDeclaredThisFrame = true
    const width = logicalWidth(), height = logicalHeight()
    const x = Math.max(0, Math.min(width - 1, numberArg(1)))
    const y = Math.max(0, Math.min(height - 1, numberArg(2)))
    const inputWidth = Math.max(1, Math.min(width - x, numberArg(3, width - x)))
    const inputHeight = Math.max(1, Math.min(height - y, numberArg(4, 40)))
    const placeholder = stringArg(5).slice(0, 256)
    const maxLength = Math.max(1, Math.min(MAX_TEXT_INPUT_LENGTH, Math.floor(numberArg(6, 512))))
    const state = textInput.value
    state.visible = true
    state.x = x
    state.y = y
    state.width = inputWidth
    state.height = inputHeight
    state.placeholder = placeholder
    state.maxLength = maxLength
    if (state.value.length > maxLength) state.value = state.value.slice(0, maxLength)
    lua.lua_pushstring(L, to_luastring(state.value))
    return 1
  })
  registerFunction('text_input_value', () => {
    lua.lua_pushstring(L, to_luastring(textInput.value.value))
    return 1
  })
  registerFunction('text_input_focus', () => {
    if (!textInput.value.visible) { lua.lua_pushboolean(L, false); return 1 }
    void nextTick(() => textInputEl.value?.focus({ preventScroll: true }))
    lua.lua_pushboolean(L, true)
    return 1
  })
  registerFunction('text_input_clear', () => {
    textInput.value.value = ''
    textInputSubmits.length = 0
    lua.lua_pushboolean(L, true)
    return 1
  })
  registerFunction('text_input_take_submit', () => {
    const submitted = textInputSubmits.shift()
    if (submitted == null) lua.lua_pushnil(L)
    else lua.lua_pushstring(L, to_luastring(submitted))
    return 1
  })
  registerFunction('text_input_hide', () => {
    hideTextInput(true)
    lua.lua_pushboolean(L, true)
    return 1
  })
  registerFunction('fullscreen_button', () => {
    // Backward-compatible capability declaration. The shell owns the control.
    lua.lua_pushboolean(L, true)
    return 1
  })
  lua.lua_setglobal(L, to_luastring('game'))

  lua.lua_newtable(L)
  registerFunction('live', () => { pushLuaValue(props.liveSnapshot()); return 1 })
  registerFunction('open_characters', () => {
    if (typeof props.openCharacters !== 'function') return lauxlib.luaL_error(L, to_luastring('open_characters_unavailable'))
    try { props.openCharacters(stringArg(1).trim()); lua.lua_pushboolean(L, true); return 1 } catch (e) { return lauxlib.luaL_error(L, to_luastring(e?.message || 'open_characters_failed')) }
  })
  registerFunction('trade_intent', () => {
    const payload = { mint: stringArg(1), method: stringArg(2), pct: Math.floor(numberArg(3)), guarded: boolArg(4, false), user_initiated: trustedPointerIntent }
    const wallet = stringArg(5); if (wallet) payload.wallet = wallet
    try { props.tradeIntent(payload); lua.lua_pushboolean(L, true); return 1 } catch (e) { return lauxlib.luaL_error(L, to_luastring(e?.message || 'trade_intent_failed')) }
  })
  registerFunction('ai_command', () => {
    if (typeof props.aiCommand !== 'function') return lauxlib.luaL_error(L, to_luastring('ai_command_unavailable'))
    const prompt = stringArg(1).trim()
    if (!prompt || prompt.length > 8000) return lauxlib.luaL_error(L, to_luastring('ai_command_invalid'))
    const requestId = `game-ai:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`
    const baseStrategyId = Math.floor(numberArg(2))
    try {
      Promise.resolve(props.aiCommand({ prompt, request_id: requestId, ...(baseStrategyId > 0 ? { base_strategy_id: baseStrategyId } : {}) }))
        .then(result => {
          if (result?.status === 'completed' || result?.status === 'failed') rememberAIResult(requestId, { ready: true, result })
        })
        .catch(e => {
          const message = e?.message || 'ai_command_failed'
          rememberAIResult(requestId, { ready: true, error: message })
          emit('status', { ok: false, message })
        })
      lua.lua_pushstring(L, to_luastring(requestId))
      return 1
    } catch (e) {
      return lauxlib.luaL_error(L, to_luastring(e?.message || 'ai_command_failed'))
    }
  })
  registerFunction('ai_result', () => {
    const requestId = stringArg(1).trim()
    if (!requestId) return lauxlib.luaL_error(L, to_luastring('ai_result_invalid'))
    const item = aiResults.get(requestId)
    if (item) {
      aiResults.delete(requestId)
      pushLuaValue(item)
      return 1
    }
    const now = Date.now()
    const lastCheckedAt = aiResultCheckedAt.get(requestId) || 0
    if (typeof props.aiResult === 'function' && !aiResultLoads.has(requestId) && now - lastCheckedAt >= AI_READ_REFRESH_MS) {
      aiResultCheckedAt.set(requestId, now)
      aiResultLoads.add(requestId)
      Promise.resolve(props.aiResult({ request_id: requestId }))
        .then(result => {
          if (result?.status === 'completed' || result?.status === 'failed') rememberAIResult(requestId, { ready: true, result })
        })
        .catch(e => {
          const message = e?.message || 'ai_result_failed'
          rememberAIResult(requestId, { ready: true, error: message })
        })
        .finally(() => aiResultLoads.delete(requestId))
    }
    lua.lua_pushnil(L)
    return 1
  })
  registerFunction('ai_history', () => {
    const now = Date.now()
    if (typeof props.aiHistory === 'function' && !aiHistoryLoading && now - aiHistoryFetchedAt >= AI_READ_REFRESH_MS) {
      aiHistoryFetchedAt = now
      aiHistoryLoading = true
      Promise.resolve(props.aiHistory())
        .then(result => { aiHistoryCache = Array.isArray(result?.items) ? result.items : [] })
        .catch(e => emit('status', { ok: false, message: e?.message || 'ai_history_failed' }))
        .finally(() => { aiHistoryLoading = false })
    }
    pushLuaValue(aiHistoryCache)
    return 1
  })
  registerFunction('reset_ability', () => {
    if (typeof props.resetAbility !== 'function') return lauxlib.luaL_error(L, to_luastring('reset_ability_unavailable'))
    const trigger = stringArg(1).trim()
    if (trigger !== 'feed_update' && trigger !== 'position_update') return lauxlib.luaL_error(L, to_luastring('reset_ability_invalid'))
    try {
      const request = props.resetAbility(trigger)
      if (request?.catch) request.catch(e => emit('status', { ok: false, message: e?.message || 'reset_ability_failed' }))
      lua.lua_pushboolean(L, true)
      return 1
    } catch (e) {
      return lauxlib.luaL_error(L, to_luastring(e?.message || 'reset_ability_failed'))
    }
  })
  registerFunction('control_intent', () => {
    if (typeof props.controlIntent !== 'function') return lauxlib.luaL_error(L, to_luastring('control_intent_unavailable'))
    try {
      const request = props.controlIntent(stringArg(1), scalarArg(2))
      if (request?.catch) request.catch(e => emit('status', { ok: false, message: e?.message || 'control_intent_failed' }))
      lua.lua_pushboolean(L, true)
      return 1
    } catch (e) {
      return lauxlib.luaL_error(L, to_luastring(e?.message || 'control_intent_failed'))
    }
  })
  lua.lua_setglobal(L, to_luastring('aime'))
}

function rememberAIResult(requestId, value) {
  if (!requestId) return
  aiResults.set(requestId, value)
  while (aiResults.size > MAX_AI_RESULTS) {
    const oldest = aiResults.keys().next().value
    if (!oldest) break
    aiResults.delete(oldest)
  }
}

function pushLuaValue(value, seen = new Set()) {
  if (value == null) { lua.lua_pushnil(L); return }
  if (typeof value === 'boolean') { lua.lua_pushboolean(L, value); return }
  if (typeof value === 'number') { lua.lua_pushnumber(L, Number.isFinite(value) ? value : 0); return }
  if (typeof value === 'string') { lua.lua_pushstring(L, to_luastring(value)); return }
  if (seen.has(value)) { lua.lua_pushnil(L); return }
  seen.add(value)
  if (Array.isArray(value)) {
    lua.lua_createtable(L, value.length, 0)
    value.forEach((item, i) => { pushLuaValue(item, seen); lua.lua_rawseti(L, -2, i + 1) })
  } else if (typeof value === 'object') {
    lua.lua_newtable(L)
    for (const [key, item] of Object.entries(value)) { pushLuaValue(item, seen); lua.lua_setfield(L, -2, to_luastring(key)) }
  } else lua.lua_pushnil(L)
  seen.delete(value)
}

const FRAME_INSTRUCTION_BUDGET = 1500000
const INTERACTION_INSTRUCTION_BUDGET = 750000
const ENTRYPOINT_CALLBACKS = ['init', 'update', 'draw', 'tap', 'pointer', 'home']

function callbackBudget(name) {
  return name === 'update' || name === 'draw'
    ? FRAME_INSTRUCTION_BUDGET
    : INTERACTION_INSTRUCTION_BUDGET
}
function resetBudget(units = INTERACTION_INSTRUCTION_BUDGET) { instructionBudget = units }
function installBudgetHook() {
  if (typeof lua.lua_sethook !== 'function') return
  lua.lua_sethook(L, state => {
    instructionBudget -= 1000
    if (instructionBudget <= 0) {
      instructionBudgetExceeded = true
      lauxlib.luaL_error(state, to_luastring('game instruction budget exceeded'))
    }
  }, lua.LUA_MASKCOUNT, 1000)
}
function installEntrypointExports() {
  if (!L) return
  if (!lua.lua_istable(L, -1)) {
    lua.lua_pop(L, 1)
    return
  }
  for (const name of ENTRYPOINT_CALLBACKS) {
    lua.lua_getfield(L, -1, to_luastring(name))
    if (lua.lua_isfunction(L, -1)) lua.lua_setglobal(L, to_luastring(name))
    else lua.lua_pop(L, 1)
  }
  lua.lua_pop(L, 1)
}
function callGlobal(name, args = []) {
  if (!L) return false
  lua.lua_getglobal(L, to_luastring(name))
  if (!lua.lua_isfunction(L, -1)) { lua.lua_pop(L, 1); return false }
  for (const value of args) lua.lua_pushnumber(L, Number(value) || 0)
  resetBudget(callbackBudget(name))
  instructionBudgetExceeded = false
  clearHostCall()
  let status
  try {
    status = lua.lua_pcall(L, args.length, 0, 0)
  } catch (cause) {
    const budgetExceeded = instructionBudgetExceeded
    instructionBudgetExceeded = false
    if (budgetExceeded) throw new Error(`game instruction budget exceeded:${name}`, { cause })
    const message = luaError('')
    const hostMessage = takeHostCallError()
    if (message) throw new Error(message, { cause })
    if (hostMessage) throw new Error(hostMessage, { cause })
    if (cause instanceof Error) throw cause
    throw new Error(`fengari_status:${String(cause)}:${name}`)
  }
  const budgetExceeded = instructionBudgetExceeded
  instructionBudgetExceeded = false
  if (status !== lua.LUA_OK) {
    const message = luaError('')
    const hostMessage = takeHostCallError()
    if (budgetExceeded) throw new Error(`game instruction budget exceeded:${name}`)
    if (message) throw new Error(message)
    if (hostMessage) throw new Error(hostMessage)
    throw new Error(`fengari_status:${String(status)}:${name}`)
  }
  clearHostCall()
  return true
}
function luaError(fallback = 'game_runtime_error') {
  if (!L || lua.lua_gettop(L) <= 0) return fallback
  const msg = lua.lua_type(L, -1) === lua.LUA_TSTRING ? to_jsstring(lua.lua_tostring(L, -1)) : fallback
  lua.lua_pop(L, 1); return msg
}

function isDraftPreview() { return Boolean(props.draftPluginId && props.draftVersionId) }
function baseAssetURL() {
  if (isDraftPreview()) {
    return `/api/plugins/authoring/plugins/${encodeURIComponent(props.draftPluginId)}/versions/${encodeURIComponent(props.draftVersionId)}/preview`
  }
  return `/api/plugins/runtime/${encodeURIComponent(props.installationId)}`
}
async function boot() {
  const epoch = ++generation
  const startedAt = performance.now()
  const timings = {}
  let stage = 'prepare'
  const timed = async (name, fn) => {
    stage = name
    const started = performance.now()
    const value = await fn()
    timings[name] = Math.round((performance.now() - started) * 10) / 10
    return value
  }
  try {
    phase.value = 'loading'
    stop(false)
    bootStartedAt = startedAt
    assetStats = { count: 0, bytes: 0 }
    startupMs = 0
    emit('metrics', { bytes: 0, startupMs: 0 })
    assetPaths = new Set()
    const readyMarks = typeof performance.getEntriesByName === 'function'
      ? performance.getEntriesByName('aime:installations-ready')
      : []
    const startMarks = typeof performance.getEntriesByName === 'function'
      ? performance.getEntriesByName('aime:installations-resolve-start')
      : []
    const readyMark = readyMarks[readyMarks.length - 1]
    const startMark = startMarks[startMarks.length - 1]
    if (readyMark && startMark && readyMark.startTime >= startMark.startTime) {
      timings.installationsResolve = Math.round((readyMark.startTime - startMark.startTime) * 10) / 10
    }
    error.value = ''
    disposed = false
    await timed('canvas', () => nextTick())
    if (epoch !== generation || disposed) return
    resizeCanvas()
    const packageSources = await timed('package', () => fetchLuaPackage())
    const source = packageSources.entrypoint
    timings.entrypoint = 0
    moduleSources = await timed('modules', () => preloadLuaModules(source, packageSources.load))
    timings.assetCount = assetStats.count
    timings.assetBytes = assetStats.bytes
    if (epoch !== generation || disposed) return
    stage = 'vm'
    const vmStarted = performance.now()
    L = lauxlib.luaL_newstate()
    openSafeLibraries(L)
    installBudgetHook()
    registerGameAPI()
    registerPackageRequire()
    timings.vm = Math.round((performance.now() - vmStarted) * 10) / 10
    stage = 'bootstrap'
    const bootstrapStarted = performance.now()
    resetBudget(500000)
    if (lauxlib.luaL_loadbuffer(L, to_luastring(source), null, to_luastring('@main.lua')) !== lua.LUA_OK) throw new Error(luaError())
    if (lua.lua_pcall(L, 0, 1, 0) !== lua.LUA_OK) throw new Error(luaError())
    installEntrypointExports()
    callGlobal('init')
    timings.bootstrap = Math.round((performance.now() - bootstrapStarted) * 10) / 10
    stage = 'snapshot'
    const snapshotStarted = performance.now()
    props.liveSnapshot()
    timings.snapshot = Math.round((performance.now() - snapshotStarted) * 10) / 10
    previous = performance.now()
    phase.value = 'live'
    firstFrameTrace = { epoch, startedAt, timings }
    if (active) frame = requestAnimationFrame(loop)
  } catch (e) {
    if (epoch === generation && !disposed) fail(e, { stage, timings, startedAt })
  }
}

async function fetchLuaPackage() {
  const res = await fetch(`${baseAssetURL()}/_aime/package?rev=${encodeURIComponent(props.revision)}`, {
    credentials: 'include',
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`package: ${res.status} ${res.statusText}`)
  const payload = await res.json()
  const assets = payload?.assets && typeof payload.assets === 'object' && !Array.isArray(payload.assets) ? payload.assets : {}
  const entrypoint = String(assets['main.lua'] || '')
  if (!entrypoint) throw new Error('lua_package_entrypoint_missing')
  recordAsset('main.lua', new TextEncoder().encode(entrypoint).byteLength)
  return {
    entrypoint,
    load: async path => {
      if (!Object.prototype.hasOwnProperty.call(assets, path)) throw new Error(`lua_module_missing:${path}`)
      const source = String(assets[path])
      recordAsset(path, new TextEncoder().encode(source).byteLength)
      return source
    },
  }
}
async function fetchLuaAsset(path) {
  const res = await fetch(assetURL(path), { credentials: 'include', headers: authHeaders() })
  if (!res.ok) throw new Error(`${path}: ${res.status} ${res.statusText}`)
  const type = String(res.headers.get('content-type') || '').toLowerCase()
  if (type && !type.includes('lua') && !type.includes('text/plain') && !type.includes('application/octet-stream')) throw new Error(`lua_module_content_type:${path}`)
  const text = await res.text()
  recordAsset(path, new TextEncoder().encode(text).byteLength)
  return text
}
function recordAsset(path, bytes = 0) {
  const key = String(path || '').trim()
  if (!key || assetPaths.has(key)) return
  assetPaths.add(key)
  assetStats.count += 1
  assetStats.bytes += Math.max(0, Number(bytes) || 0)
  if (assetTelemetryTimer) window.clearTimeout(assetTelemetryTimer)
  const epoch = generation
  assetTelemetryTimer = window.setTimeout(() => {
    assetTelemetryTimer = 0
    if (disposed || epoch !== generation) return
    emit('metrics', { bytes: assetStats.bytes, startupMs })
    reportClientError('info', 'gamehost_assets', {
      mode: isDraftPreview() ? 'draft' : 'installed',
      installationId: isDraftPreview() ? '' : props.installationId,
      count: assetStats.count,
      bytes: assetStats.bytes,
      settledMs: bootStartedAt ? Math.round((performance.now() - bootStartedAt) * 10) / 10 : 0,
    })
  }, 500)
}
function loop(now) {
  if (disposed || !active || !L) return
  try {
    resizeCanvas()
    const dt = Math.min(0.1, Math.max(0, (now - previous) / 1000)); previous = now
    textInputDeclaredThisFrame = false
    callGlobal('update', [dt])
    callGlobal('draw')
    if (firstFrameTrace && firstFrameTrace.epoch === generation) {
      const trace = firstFrameTrace
      firstFrameTrace = null
      trace.timings.firstFrame = Math.round((performance.now() - trace.startedAt) * 10) / 10
      startupMs = trace.timings.firstFrame
      emit('metrics', { bytes: assetStats.bytes, startupMs })
      trace.timings.assetCount = assetStats.count
      trace.timings.assetBytes = assetStats.bytes
      reportClientError('info', 'gamehost_startup', {
        mode: isDraftPreview() ? 'draft' : 'installed',
        installationId: isDraftPreview() ? '' : props.installationId,
        timings: trace.timings,
      })
      emit('status', {
        ok: true,
        message: isDraftPreview() ? 'game draft live' : 'game live',
        stage: 'ready',
        timings: trace.timings,
      })
    }
    if (!textInputDeclaredThisFrame && textInput.value.visible) hideTextInput(true)
    frame = requestAnimationFrame(loop)
  } catch (e) { fail(e) }
}
function openHome() {
  if (!L || phase.value !== 'live') return false
  return callGlobal('home')
}
defineExpose({ openHome })

function pause() {
  textInputEl.value?.blur()
  active = false
  if (frame) cancelAnimationFrame(frame)
  frame = 0
  previous = 0
}
function ensureFrame() {
  if (disposed || !active || !L || frame || phase.value !== 'live') return
  previous = performance.now()
  frame = requestAnimationFrame(loop)
}
function resume() {
  active = true
  ensureFrame()
}
function recoverFrame() {
  if (document.visibilityState === 'hidden') return
  if (frame) cancelAnimationFrame(frame)
  frame = 0
  ensureFrame()
}
let trustedPointerIntent = false
function onPointer(event) {
  if (!L || !canvas.value) return
  const rect = canvas.value.getBoundingClientRect(); const x = event.clientX - rect.left; const y = event.clientY - rect.top
  trustedPointerIntent = event.isTrusted === true
  try { if (!callGlobal('pointer', [x, y, event.button + 1])) callGlobal('tap', [x, y]) } catch (e) { fail(e) } finally { trustedPointerIntent = false }
}
function onNativeTextInput(event) {
  const maxLength = textInput.value.maxLength || 1
  textInput.value.value = String(event?.target?.value || '').slice(0, maxLength)
}
function onNativeTextInputKeydown(event) {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
  event.preventDefault()
  const value = textInput.value.value.trim()
  if (!value) return
  textInputSubmits.push(value)
  while (textInputSubmits.length > MAX_TEXT_INPUT_SUBMITS) textInputSubmits.shift()
}
function hideTextInput(clear = false) {
  textInputEl.value?.blur()
  textInput.value.visible = false
  if (clear) textInput.value.value = ''
  textInputSubmits.length = 0
}
function logicalWidth() { return canvas.value?.clientWidth || 1 }
function logicalHeight() { return canvas.value?.clientHeight || 1 }
function resizeCanvas() {
  if (!canvas.value) return
  const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1)); const w = Math.max(1, canvas.value.clientWidth); const h = Math.max(1, canvas.value.clientHeight)
  const pw = Math.round(w * dpr); const ph = Math.round(h * dpr)
  if (canvas.value.width !== pw || canvas.value.height !== ph) { canvas.value.width = pw; canvas.value.height = ph; ctx = canvas.value.getContext('2d'); ctx?.setTransform(dpr, 0, 0, dpr, 0, 0) }
}
function clearCanvasSurface() {
  const node = canvas.value
  if (!node) return
  const surface = ctx || node.getContext('2d')
  if (!surface) return
  surface.save()
  surface.setTransform(1, 0, 0, 1, 0, 0)
  surface.clearRect(0, 0, node.width, node.height)
  surface.restore()
}
function cleanAssetPath(raw) {
  const value = String(raw || '').replace(/^\/+/, '')
  if (!value || value.includes('..') || value.includes('\\') || value.startsWith('.')) return ''
  return value
}
function assetURL(path) {
  const clean = cleanAssetPath(path)
  if (!clean) throw new Error('invalid_asset_path')
  const encoded = clean.split('/').map(encodeURIComponent).join('/')
  return `${baseAssetURL()}/${encoded}?rev=${encodeURIComponent(props.revision)}`
}
function ensureImage(path) {
  if (images.has(path)) return images.get(path)
  const epoch = generation
  const item = { image: new Image(), objectUrl: '' }; images.set(path, item)
  fetch(assetURL(path), { credentials: 'include', headers: authHeaders() })
    .then(res => { if (!res.ok) throw new Error(`asset ${path}: ${res.status}`); return res.blob() })
    .then(blob => {
      if (disposed || epoch !== generation) return
      recordAsset(path, blob.size)
      item.objectUrl = URL.createObjectURL(blob)
      item.image.src = item.objectUrl
    })
    .catch(e => { if (!disposed && epoch === generation) emit('status', { ok: false, message: e?.message || 'asset_load_failed' }) })
  return item
}
function ensureAudio(path) {
  if (sounds.has(path)) return sounds.get(path)
  const epoch = generation
  const item = { audio: null, objectUrl: '' }; sounds.set(path, item)
  fetch(assetURL(path), { credentials: 'include', headers: authHeaders() })
    .then(res => { if (!res.ok) throw new Error(`audio ${path}: ${res.status}`); return res.blob() })
    .then(blob => {
      if (disposed || epoch !== generation) return
      recordAsset(path, blob.size)
      item.objectUrl = URL.createObjectURL(blob)
      item.audio = new Audio(item.objectUrl)
      item.audio.preload = 'auto'
    })
    .catch(e => { if (!disposed && epoch === generation) emit('status', { ok: false, message: e?.message || 'audio_load_failed' }) })
  return item
}
function ensureMarketImage(mint) {
  const url = marketImageURL(mint)
  if (!url) return null
  const cacheKey = `${mint}:${url}`
  if (marketImages.has(cacheKey)) return marketImages.get(cacheKey)
  for (const key of marketImages.keys()) if (key.startsWith(`${mint}:`) && key !== cacheKey) marketImages.delete(key)
  const item = { image: new Image() }
  item.image.referrerPolicy = 'no-referrer'
  item.image.src = url
  item.image.onerror = () => marketImages.delete(cacheKey)
  marketImages.set(cacheKey, item)
  return item
}
function marketImageURL(mint) {
  const snapshot = props.liveSnapshot() || {}
  const rows = [...(Array.isArray(snapshot.mints) ? snapshot.mints : []), ...(Array.isArray(snapshot.coins) ? snapshot.coins : []), snapshot.pad].filter(Boolean)
  const coin = rows.find(row => String(row?.mint || '').trim() === mint)
  const normalized = normalizeImageSrc(coin?.image)
  if (!normalized) return ''
  try {
    const url = new URL(normalized, window.location.origin)
    if (url.origin === window.location.origin) return url.href
    return url.protocol === 'https:' ? url.href : ''
  } catch {
    return ''
  }
}
function fail(e, trace = {}) {
  const reason = String(e?.message || e || 'game_runtime_error')
  const stage = String(trace.stage || (phase.value === 'live' ? 'frame' : 'runtime'))
  error.value = `${stage}: ${reason}`
  phase.value = 'error'
  const timings = { ...(trace.timings || {}) }
  if (trace.startedAt) timings.failedAt = Math.round((performance.now() - trace.startedAt) * 10) / 10
  emit('status', { ok: false, message: error.value, stage, reason, timings })
  reportClientError('error', 'gamehost_runtime', {
    mode: isDraftPreview() ? 'draft' : 'installed',
    installationId: isDraftPreview() ? '' : props.installationId,
    stage,
    reason,
    timings,
  })
  firstFrameTrace = null
  generation += 1
  stop()
}
function stop(dispose = true) {
  if (frame) cancelAnimationFrame(frame); frame = 0; previous = 0
  if (assetTelemetryTimer) window.clearTimeout(assetTelemetryTimer); assetTelemetryTimer = 0
  firstFrameTrace = null
  if (dispose) disposed = true
  moduleSources = new Map(); moduleRefs.clear(); moduleLoading.clear()
  aiResultLoads.clear(); aiResultCheckedAt.clear()
  aiHistoryCache = []; aiHistoryFetchedAt = 0
  hideTextInput(true)
  clearHostCall()
  clearCanvasSurface()
  L = null; ctx = null
  for (const item of images.values()) if (item.objectUrl) URL.revokeObjectURL(item.objectUrl)
  images.clear()
  marketImages.clear()
  for (const item of sounds.values()) {
    if (item.audio) { item.audio.pause(); item.audio.src = '' }
    if (item.objectUrl) URL.revokeObjectURL(item.objectUrl)
  }
  sounds.clear()
}
watch(() => [props.installationId, props.draftPluginId, props.draftVersionId, props.revision], () => boot())
onMounted(() => {
  active = true
  window.addEventListener('pageshow', recoverFrame)
  document.addEventListener('visibilitychange', recoverFrame)
  boot()
})
onActivated(() => resume())
onDeactivated(() => pause())
onBeforeUnmount(() => {
  window.removeEventListener('pageshow', recoverFrame)
  document.removeEventListener('visibilitychange', recoverFrame)
  generation += 1
  stop()
})
</script>

<style scoped>
.game-host{position:relative;flex:1 1 auto;width:100%;height:100%;min-width:0;min-height:0;background:#050505;overflow:hidden;touch-action:none}.game-text-input{position:absolute;z-index:6;box-sizing:border-box;resize:none;overflow:auto;margin:0;padding:9px 11px;border:1px solid #ffffff2b;border-radius:10px;outline:none;background:#081117e8;color:#f8fafc;font:14px/1.35 ui-sans-serif,system-ui,sans-serif;touch-action:manipulation;-webkit-user-select:text;user-select:text;box-shadow:0 4px 18px #0008}.game-text-input::placeholder{color:#82909f}.game-text-input:focus{border-color:#7dd3fc8f;box-shadow:0 0 0 2px #38bdf826,0 4px 18px #0008}.game-canvas{display:block;width:100%;height:100%;min-height:0}.game-loading{position:absolute;inset:0;z-index:4;display:flex;align-items:center;justify-content:center;gap:8px;background:#050505;color:#93a0b4;font-size:12px;font-weight:800}.game-loading-dot{width:7px;height:7px;border-radius:999px;background:#22c55e;box-shadow:0 0 10px rgba(34,197,94,.55);animation:game-loading-pulse 1.1s ease-in-out infinite}@keyframes game-loading-pulse{0%,100%{opacity:.35;transform:scale(.85)}50%{opacity:1;transform:scale(1)}}.game-error{position:absolute;inset:16px;display:grid;place-content:center;gap:6px;text-align:center;color:#fecaca;background:#120808dd;border:1px solid #5f2323;border-radius:10px;padding:16px}.game-error strong{font-size:13px}.game-error span{font:11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;overflow-wrap:anywhere}
</style>
